/* ═══ CONTACT-MATCH COMMIT ═══
   Turns the operator's Link / New / No-Link decisions into CRM writes.

   Background: the contact-match review panel has been shipping decisions since
   2026-06-01 and nothing has ever consumed them. `rejectContactMatch` is
   commented "create a fresh record on book" — that consumer was never written.
   As of 2026-08-26 the live CRM shows the cost: the most recent contact created
   in the system is dated 2026-07-13, while 15 quotes have gone through the
   builder since. 14 people were explicitly marked "New" and never created.

   This module is the missing consumer, split in two so the decisions are
   testable without Firestore:

     planContactCommits()  — PURE. Reads the decisions + the current CRM and
                             returns the exact list of writes to perform.
     (the executor)        — lives in DataContext, awaits saveContact/saveClient
                             in the order this planner returns.

   The company is always planned before its people, so a contact can carry the
   `clientId` of the client created in the same pass (CD6). */

import { CONSENT_RANK, consentLocked, isConsentDate, makeConsent, readConsent, setConsent } from "./consent.js";
import {
  emailDomain,
  isOrgDomain,
  normalizeEmail,
  normalizeName,
  resolveClient,
  resolveContact,
} from "./identity.js";

/* The consent state a quote row implies. The person the operator confirmed as
   the contact of record asked us for a price, which is a relationship; a cc'd
   person was copied on somebody else's email and asked us for nothing. Neither
   is ever an opt-in — only the website, Mailchimp, or an operator recording an
   offline consent by hand can assert that (src/lib/consent.js). */
const stateForRow = (ex) => ((ex?.role || "contact") === "cc" ? "no_bulk" : "relationship");

/**
 * The date a consent write records.
 *
 * `quote.quoteDate` is operator data. Every one of the 132 live quotes carries
 * a date-only string today, but nothing enforces that, and setConsent() throws
 * on anything that is not a strict YYYY-MM-DD. planContactCommits() runs
 * OUTSIDE the try/catch in DataContext's commitContactMatches, so a malformed
 * date here would turn a CRM detail into a failed quote save or a failed
 * booking. Take the date half, check that it is a real calendar date, and fall
 * back to today rather than throwing.
 */
export function consentDate(quoteDate, now) {
  const fromQuote = String(quoteDate ?? "").trim().slice(0, 10);
  if (isConsentDate(fromQuote)) return fromQuote;
  const fromNow = String(now ?? "").trim().slice(0, 10);
  if (isConsentDate(fromNow)) return fromNow;
  return new Date().toISOString().slice(0, 10);
}

/** setConsent(), which throws by design, made safe for the save path: any
 *  refusal means "leave the consent alone", never a thrown quote save. */
function safeConsent(record, params) {
  try {
    return setConsent(record, params);
  } catch {
    return null;
  }
}

/**
 * The consent to stamp on a contact from one quote row, or null for
 * "leave it alone". A quote is weak evidence: it may raise a record that has
 * said nothing yet, and it may never touch one that has.
 *
 *   - `opted_in` or `unsubscribed: true` is the person's own answer: untouched.
 *   - a cc row never demotes someone already known as a relationship.
 */
function consentForRow(existing, ex, at) {
  const state = stateForRow(ex);
  if (consentLocked(existing)) return null;
  if (CONSENT_RANK[state] <= CONSENT_RANK[readConsent(existing).state]) return null;
  return safeConsent(existing, { state, source: "quote", at });
}

/** Default id factory. Matches the `ct_`/`cl_` v2 format in data/crm.md.
 *  Injectable so tests get deterministic ids. */
const defaultMkId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

/**
 * Plan the CRM writes implied by a quote's contactMatches.
 *
 * @param quote     the quote being saved/booked (for industry, leadSource, dates)
 * @param matches   quote.contactMatches — the lean review-panel shape
 * @param contacts  current CRM contacts
 * @param clients   current CRM clients
 * @param opts.mkId (prefix) => id
 * @param opts.now  ISO timestamp
 * @returns {{ ops, contactEntityId, companyEntityId, linkedContactIds, skipped }}
 *   ops: [{ kind: "client"|"contact", mode: "create"|"update", record, reason }]
 *        — apply in order; the client op (if any) always comes first.
 */
export function planContactCommits(quote, matches, contacts, clients, opts = {}) {
  const mkId = opts.mkId || defaultMkId;
  const now = opts.now || new Date().toISOString();
  const quoteDate = quote?.quoteDate || now.slice(0, 10);
  /* Separate from quoteDate on purpose: firstContact/lastContact are free-form
     CRM strings that tolerate whatever the quote holds, while a consent date is
     validated and can refuse. */
  const consentAt = consentDate(quoteDate, now);

  const ops = [];
  const skipped = [];
  const linkedContactIds = [];

  // Working copies so a second row in the same pass sees the first row's record
  // and links to it instead of minting a twin.
  let workContacts = [...(contacts || [])];
  let workClients = [...(clients || [])];

  // Seed from the quote's already-linked client (the ClientPicker path), so a
  // person confirmed WITHOUT a separate company row still lands on the client
  // the operator picked (Codex review 2026-08-26, F5). Validated against the
  // live list — a stale id must not be propagated onto new contacts. An explicit
  // confirmed company row in Pass 1 overrides this seed.
  let companyEntityId = (quote?.companyEntityId && workClients.some((c) => c.id === quote.companyEntityId))
    ? quote.companyEntityId
    : null;

  const rows = (matches || []).filter((m) => {
    if (!m) return false;
    if (m.ignored) { skipped.push({ row: m, reason: "no-link" }); return false; }
    // An undecided row is not a commitment. The operator hasn't looked at it,
    // and guessing on their behalf is what fills a CRM with junk.
    if (!m.confirmed) { skipped.push({ row: m, reason: "undecided" }); return false; }
    return true;
  });

  const isCompanyRow = (m) => (m.extracted?.role || "") === "client";

  /* ── Pass 1: the company ────────────────────────────────────────────────── */

  for (const m of rows.filter(isCompanyRow)) {
    const ex = m.extracted || {};
    const email = normalizeEmail(ex.email);
    const domain = isOrgDomain(ex.url) ? ex.url : emailDomain(email);

    // An explicit Link is authoritative — but only if the record still exists.
    // A deleted CRM record must not leave a dangling id on the quote.
    let existing = m.matchId ? workClients.find((c) => c.id === m.matchId) : null;

    // "New" still checks for an existing account first (CD5). The operator is
    // saying "this isn't the record you suggested", not "I know there is none".
    if (!existing) existing = resolveClient({ name: ex.name, domain, email }, workClients).record;

    if (existing) {
      companyEntityId = existing.id;
      const patch = mergeClient(existing, { email, domain, quoteDate });
      if (patch) {
        ops.push({ kind: "client", mode: "update", record: patch, reason: "link" });
        workClients = workClients.map((c) => (c.id === patch.id ? patch : c));
      }
      continue;
    }

    if (!String(ex.name || "").trim()) { skipped.push({ row: m, reason: "no-name" }); continue; }

    const created = {
      id: mkId("cl"),
      name: ex.name.trim(),
      clientCode: "",
      industry: [quote?.industry || "other"],
      billingAddress: "",
      email: email || "",
      phone: "",
      status: "active",
      leadSource: quote?.leadSource || "",
      referredBy: quote?.referredBy || "",
      // The durable org key. Never written before this module existed, which is
      // why domain-tier matching had nothing to match against.
      orgDomain: isOrgDomain(domain) ? domain : "",
      parentClientId: "",
      firstContact: quoteDate,
      lastContact: quoteDate,
      notes: quote?.id ? `Auto-created from quote ${quote.id}` : "",
      linkedContacts: [],
      crmOrigin: { role: ex.role || "client", source: ex.source || "", quoteId: quote?.id || null, at: now },
      createdAt: now,
    };
    ops.push({ kind: "client", mode: "create", record: created, reason: "new" });
    workClients = [...workClients, created];
    companyEntityId = created.id;
  }

  /* ── Pass 2: the people ─────────────────────────────────────────────────── */

  // The primary contact is the one the AI tagged as such; a cc'd person is
  // recorded and linked but never promoted to the quote's contact of record.
  const primaryCandidates = [];
  for (const m of rows.filter((r) => !isCompanyRow(r))) {
    const ex = m.extracted || {};
    const email = normalizeEmail(ex.email);

    let existing = m.matchId ? workContacts.find((c) => c.id === m.matchId) : null;
    if (!existing) existing = resolveContact({ email, name: ex.name }, workContacts).record;

    if (existing) {
      const patch = mergeContact(existing, { email, ex, quoteDate, consentAt, companyEntityId, quote });
      if (patch) {
        ops.push({ kind: "contact", mode: "update", record: patch, reason: "link" });
        workContacts = workContacts.map((c) => (c.id === patch.id ? patch : c));
      }
      linkedContactIds.push(existing.id);
      if ((ex.role || "contact") === "contact") primaryCandidates.push({ m, id: existing.id });
      continue;
    }

    if (!String(ex.name || "").trim()) { skipped.push({ row: m, reason: "no-name" }); continue; }

    const created = {
      id: mkId("ct"),
      name: ex.name.trim(),
      emails: email ? [email] : [],
      phone: "",
      company: quote?.clientName || "",
      // CD6 — the structural association. `company` above stays display text.
      clientId: companyEntityId || null,
      industry: [quote?.industry || "other"],
      role: ["prospect"],
      leadSource: quote?.leadSource || "",
      referredBy: quote?.referredBy || "",
      score: 50,
      threadCount: 1,
      firstContact: quoteDate,
      lastContact: quoteDate,
      notes: quote?.id ? `Auto-created from quote ${quote.id}` : "",
      // Durable provenance. A person who arrived as a cc on someone else's email
      // is not a person who asked to hear from us — the Phase 3 categorization
      // pass and the mailing-list rule both read this, so it has to survive the
      // write rather than living only in the quote's contactMatches array.
      crmOrigin: { role: ex.role || "contact", source: ex.source || "", quoteId: quote?.id || null, at: now },
      // CRM v3 consent. The primary contact on a quote is a relationship; a
      // cc'd person is not. Legacy mailchimpStatus is still written until
      // slice 5 retires it, and still says the same thing: not mailable.
      consent: safeConsent({}, { state: stateForRow(ex), source: "quote", at: consentAt })
        // A refusal here is not a reason to write nothing: the floor is the
        // state that grants nothing.
        || makeConsent({ state: "no_bulk", source: "quote", at: consentAt }),
      mailchimpStatus: "unsubscribed",
      lastCampaignDate: "",
      campaignCount: 0,
      linkedClients: companyEntityId ? [{ clientId: companyEntityId, linkType: "employee" }] : [],
      createdAt: now,
    };
    ops.push({ kind: "contact", mode: "create", record: created, reason: "new" });
    workContacts = [...workContacts, created];
    linkedContactIds.push(created.id);
    if ((ex.role || "contact") === "contact") primaryCandidates.push({ m, id: created.id });
  }

  // The quote's contact of record is the confirmed person named in its Contact
  // field (the same row quoteHasLinkedContact accepts), so the saved name and
  // link are one person; with no such row, the first confirmed contact.
  const typed = normalizeName(quote?.contactName || "");
  const named = typed && primaryCandidates.find(({ m }) => (
    normalizeName(m.extracted?.name) === typed || (m.matchName && normalizeName(m.matchName) === typed)
  ));
  const contactEntityId = (named || primaryCandidates[0])?.id || null;

  /* ── Pass 3: back-link the people onto the client ───────────────────────── */

  if (companyEntityId && linkedContactIds.length) {
    const clientOp = ops.find((o) => o.kind === "client" && o.record.id === companyEntityId);
    const base = clientOp?.record || workClients.find((c) => c.id === companyEntityId);
    if (base) {
      const have = new Set((base.linkedContacts || []).map((l) => l.contactId));
      const add = linkedContactIds.filter((id) => !have.has(id));
      if (add.length) {
        const next = {
          ...base,
          linkedContacts: [...(base.linkedContacts || []), ...add.map((id) => ({ contactId: id, role: "contact" }))],
        };
        if (clientOp) clientOp.record = next;
        else ops.push({ kind: "client", mode: "update", record: next, reason: "backlink" });
      }
    }
  }

  return { ops, contactEntityId, companyEntityId, linkedContactIds: uniq(linkedContactIds), skipped };
}

/** Strip references to clients whose create FAILED, so a contact is never
 *  written holding a clientId that points at a record that doesn't exist
 *  (Gemini review 2026-08-26, G3). Pure; used by the executor per-op. */
export function stripFailedClientRefs(record, failedClientIds) {
  if (!failedClientIds || failedClientIds.size === 0) return record;
  const next = { ...record };
  let changed = false;
  if (next.clientId && failedClientIds.has(next.clientId)) { next.clientId = null; changed = true; }
  if ((next.linkedClients || []).some((l) => failedClientIds.has(l.clientId))) {
    next.linkedClients = next.linkedClients.filter((l) => !failedClientIds.has(l.clientId));
    changed = true;
  }
  return changed ? next : record;
}

/* ── Merge helpers ───────────────────────────────────────────────────────────
   Additive only. An existing CRM record is the operator's data; a quote may add
   a missing address or advance `lastContact`, and may never overwrite a value
   that is already there. Both return null when there is nothing to write, so
   linking a known contact costs zero Firestore writes. */

function mergeClient(existing, { email, domain, quoteDate }) {
  const next = { ...existing };
  let changed = false;

  if (email && !normalizeEmail(existing.email)) { next.email = email; changed = true; }
  if (isOrgDomain(domain) && !isOrgDomain(existing.orgDomain)) { next.orgDomain = domain; changed = true; }
  if (quoteDate && (!existing.lastContact || existing.lastContact < quoteDate)) {
    next.lastContact = quoteDate; changed = true;
  }
  if (quoteDate && !existing.firstContact) { next.firstContact = quoteDate; changed = true; }

  return changed ? next : null;
}

function mergeContact(existing, { email, ex, quoteDate, consentAt, companyEntityId, quote }) {
  const next = { ...existing };
  let changed = false;

  // A second address for a known person: append, never replace. 58 same-person
  // pairs in the live CRM exist precisely because a second address became a
  // second record.
  if (email) {
    const have = (existing.emails || []).map(normalizeEmail).filter(Boolean);
    if (!have.includes(email)) { next.emails = [...(existing.emails || []), email]; changed = true; }
  }
  if (companyEntityId && !existing.clientId) { next.clientId = companyEntityId; changed = true; }
  if (companyEntityId && !(existing.linkedClients || []).some((l) => l.clientId === companyEntityId)) {
    next.linkedClients = [...(existing.linkedClients || []), { clientId: companyEntityId, linkType: "employee" }];
    changed = true;
  }
  if (!existing.company && quote?.clientName) { next.company = quote.clientName; changed = true; }
  if (quoteDate && (!existing.lastContact || existing.lastContact < quoteDate)) {
    next.lastContact = quoteDate; changed = true;
  }
  if (quoteDate && !existing.firstContact) { next.firstContact = quoteDate; changed = true; }
  if (!existing.crmOrigin && (ex?.role || ex?.source)) {
    next.crmOrigin = { role: ex.role || "contact", source: ex.source || "", quoteId: quote?.id || null, at: quoteDate };
    changed = true;
  }
  /* Consent rises when the operator links a known person as the contact of
     record on a quote — that is a relationship whether or not the CRM knew it
     yet. consentForRow() returns null when nothing may change: a cc row, an
     existing opt-in, an unsubscribe. Additive like everything else here. */
  const consent = consentForRow(existing, ex, consentAt);
  if (consent) { next.consent = consent; changed = true; }

  return changed ? next : null;
}
